import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { ENABLED_DEAL_SENSITIVE_CATEGORIES } from '@openx/constants/feature-flag-keys.ts';
import type { RowActionProps } from '@openx/types/tableHelpers.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useCallback, useEffectEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../../components/AuthContext/context.ts';
import { isAdmin, isInternalAdmin } from '../../permissions/validators.ts';
import { useListCriteria } from '../../utils/useListCriteria.ts';
import { DealDecidedSensitiveCategoriesList } from './components/DealDecidedSensitiveCategoriesList.tsx';
import { DealProposedSensitiveCategoriesList } from './components/DealProposedSensitiveCategoriesList.tsx';
import { DealsSensitiveCategoriesFiltersTopBar } from './components/DealSensitiveCategoriesFiltersTopBar.tsx';
import { DealsSensitiveCategoriesBulkOperations } from './components/DealsSensitiveCategoriesBulkOperations.tsx';
import {
  defaultDecidedSensitiveCategoriesCriteria,
  defaultProposedSensitiveCategoriesCriteria,
  defaultSensitiveCategoriesListFitlers,
} from './constants.tsx';
import { fakeDealAdCategoryProposals } from './fake.data.ts';
import {
  SENSITIVE_CATEGORIES_TABS,
  type SensitiveCategoriesTabs,
  useSensitiveCategoriesTabs,
} from './hooks/useSensitiveCategoriesTabs.ts';
import type { DealAdCategoryProposal } from './model.ts';
import { DealAdCategoryProposalStatus } from './model.ts';
import type { DealsSensitiveCategoriesListFilters } from './types.ts';

// A single dealAdCategoryProposals query filters by status server-side (EXCH-18569) — until that query is
// wired up, mimic the same filtering over the fake dataset so each tab reflects only its own status.
const STATUS_BY_TAB: Record<SensitiveCategoriesTabs, DealAdCategoryProposalStatus> = {
  [SENSITIVE_CATEGORIES_TABS.Proposed]: DealAdCategoryProposalStatus.Pending,
  [SENSITIVE_CATEGORIES_TABS.Applied]: DealAdCategoryProposalStatus.Accepted,
  [SENSITIVE_CATEGORIES_TABS.Rejected]: DealAdCategoryProposalStatus.Rejected,
};

export function DealsSensitiveCategoriesPage() {
  const { t } = useTranslation();
  const location = useLocation();

  const { session } = useAuth();
  const isAllowed = isAdmin(session) && isInternalAdmin(session);

  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isFeatureEnabled = isGrowthbookFeatureOn(ENABLED_DEAL_SENSITIVE_CATEGORIES);

  const { activeTab, changeTab } = useSensitiveCategoriesTabs(location.pathname);
  const isProposedTab = activeTab === 'proposed';

  const [selectedCategories, setSelectedCategories] = useState<DealAdCategoryProposal[]>([]);
  const { criteria, setCriteria } = useListCriteria<'sensitiveCategories'>(
    'sensitiveCategories',
    isProposedTab ? defaultProposedSensitiveCategoriesCriteria : defaultDecidedSensitiveCategoriesCriteria
  );
  const filters = criteria.filters ?? defaultSensitiveCategoriesListFitlers;

  const proposedCategories = useMemo(
    () =>
      fakeDealAdCategoryProposals.filter(
        proposal => proposal.status === STATUS_BY_TAB[SENSITIVE_CATEGORIES_TABS.Proposed]
      ),
    []
  );
  const items = useMemo(
    () => fakeDealAdCategoryProposals.filter(proposal => proposal.status === STATUS_BY_TAB[activeTab]),
    [activeTab]
  );

  const List = isProposedTab ? DealProposedSensitiveCategoriesList : DealDecidedSensitiveCategoriesList;
  const firstTabName = t('Proposed Sensitive Categories');
  const firstTabLabel = proposedCategories.length ? `${firstTabName} [${proposedCategories.length}]` : firstTabName;

  const bulkOperationRender = useCallback(
    () => (
      <DealsSensitiveCategoriesBulkOperations
        selectedCategories={selectedCategories}
        closeAndUnselect={() => setSelectedCategories([])}
      />
    ),
    [selectedCategories]
  );

  const rowActions = useMemo(() => {
    const actions: RowActionProps<DealAdCategoryProposal>[] = [
      {
        label: t('Apply'),
        onClick: () => {
          // TODO: EXCH-18744
        },
      },
      {
        label: t('Reject'),
        onClick: () => {
          // TODO: EXCH-18744
        },
      },
    ];
    return actions;
  }, [t]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setCriteria should not trigger tab change
  const onTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      changeTab(newValue as (typeof SENSITIVE_CATEGORIES_TABS)[keyof typeof SENSITIVE_CATEGORIES_TABS]);
      setSelectedCategories([]);
      setCriteria(
        newValue === 'proposed' ? defaultProposedSensitiveCategoriesCriteria : defaultDecidedSensitiveCategoriesCriteria
      );
    },
    [changeTab]
  );

  const onToggleSelect = useCallback(
    (nextItems, operation) => {
      const nextSelectedCategories = applySelectionChange({
        changeItems: nextItems,
        itemsComparer: (left, right) => left.id === right.id,
        operation,
        previouslySelectedItems: selectedCategories,
      });
      setSelectedCategories(nextSelectedCategories);
    },
    [selectedCategories]
  );

  const resetSelectedCategoriesWhenNotEmpty = useEffectEvent(() => {
    if (selectedCategories.length > 0) {
      setSelectedCategories([]);
    }
  });

  const handleCriteriaChange = useCallback(
    nextCriteria => {
      resetSelectedCategoriesWhenNotEmpty();

      if (nextCriteria?.dimension === 'sort') {
        nextCriteria.sort = nextCriteria.value;
      }

      setCriteria(prev => ({
        ...prev,
        ...nextCriteria,
      }));
    },
    [setCriteria]
  );

  const handleFiltersChange = useCallback(
    (nextFilters: DealsSensitiveCategoriesListFilters) => {
      handleCriteriaChange({
        filters: nextFilters,
      });
    },
    [handleCriteriaChange]
  );

  if (!isFeatureEnabled || !isAllowed) {
    return <Navigate to="/" />;
  }

  return (
    <PageHeader
      sticky
      titlePrefix="OpenXSelect"
      title={t('Deals Sensitive Categories')}
      emptyMode={!!selectedCategories.length}
      emptyModeRender={bulkOperationRender}
    >
      <Tabs data-test="deals-sensitive-categories-list-tabs" onChange={onTabChange} value={activeTab}>
        <Tab data-test="proposed-categories-tab" label={firstTabLabel} value={SENSITIVE_CATEGORIES_TABS.Proposed} />
        <Tab
          data-test="applied-categories-tab"
          label={t('Applied Sensitive Categories')}
          value={SENSITIVE_CATEGORIES_TABS.Applied}
        />
        <Tab
          data-test="rejected-categories-tab"
          label={t('Rejected Sensitive Categories')}
          value={SENSITIVE_CATEGORIES_TABS.Rejected}
        />
      </Tabs>
      <DealsSensitiveCategoriesFiltersTopBar filters={filters} onChange={handleFiltersChange} />
      <List
        activeTab={activeTab}
        items={items}
        selectedItems={selectedCategories}
        criteria={criteria}
        rowActions={rowActions}
        onCriteriaChange={handleCriteriaChange}
        onSelectChange={onToggleSelect}
      />
    </PageHeader>
  );
}
